import React, { useState, useMemo } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import Modal from '../../../ui-kit/Modal';
import Button from '../../../ui-kit/Button';
import { AlertTriangle, UserCheck } from 'lucide-react';
import { ISSUE_TYPES, DEPARTMENTS, PRIORITIES, SOURCES, categoryForIssue } from '../constants';
import NumberedSearchSelect from '../../../shared/components/NumberedSearchSelect';
import { inferPriority, departmentForIssue } from '../logic/priority';
import { findDuplicate, countCustomerComplaints, isRepeatComplaintCustomer } from '../logic/duplicate';
import { TicketService } from '../services/ticketService';

const normMobile = (m) => String(m || '').replace(/\D/g, '').slice(-10);
const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-200 outline-none';

const CreateTicketModal = ({ isOpen, onClose, currentUser, existingTickets = [], onCreated, prefill = {} }) => {
    const [form, setForm] = useState({
        customerName: '', mobile: '', orderId: '', issueType: 'Other Complaint',
        description: '', source: 'manual', priority: '', assignedDepartment: '',
        assignedTo: '', followUpAt: '', productNames: '',
        ...prefill,
    });
    const [saving, setSaving] = useState(false);
    const [files, setFiles] = useState([]); // optional attachments (multiple)
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    // Paste a screenshot / image anywhere in the form → add it as an attachment.
    const onPasteImage = (e) => {
        const imgs = [...(e.clipboardData?.items || [])].filter(it => it.kind === 'file' && it.type.startsWith('image/'));
        if (!imgs.length) return;
        const fs = imgs.map((it, k) => { const f = it.getAsFile(); return f && !f.name ? new File([f], `pasted-${Date.now()}-${k}.png`, { type: f.type }) : f; }).filter(Boolean);
        if (fs.length) setFiles(prev => [...prev, ...fs]);
    };

    // Customer context derived from the entered mobile.
    const mobile10 = normMobile(form.mobile);
    const priorComplaints = useMemo(
        () => existingTickets.filter(t => normMobile(t.mobile) === mobile10 && mobile10),
        [existingTickets, mobile10]
    );
    const repeat = isRepeatComplaintCustomer(form.mobile, existingTickets);
    const duplicate = useMemo(() => mobile10
        ? findDuplicate({ mobile: form.mobile, orderId: form.orderId, issueType: form.issueType }, existingTickets)
        : null, [form.mobile, form.orderId, form.issueType, existingTickets, mobile10]);

    const autoFetchCustomer = async () => {
        if (!mobile10) return;
        try {
            // Healed lookup: fills the name from orders when the customer record is a stub.
            const { CustomerService } = await import('../../customers/services/CustomerService');
            const c = await CustomerService.getCustomer(mobile10);
            const name = c?.name || c?.primaryName || '';
            if (name && name !== 'Unknown') set('customerName', name);
        } catch (e) { /* non-fatal */ }
    };

    const effPriority = form.priority || inferPriority(form.issueType, { isRepeatCustomer: repeat });
    const effDept = form.assignedDepartment || departmentForIssue(form.issueType);

    const submit = async () => {
        if (!form.mobile.trim()) return;
        setSaving(true);
        try {
            const created = await TicketService.createTicket({
                customerName: form.customerName,
                mobile: form.mobile,
                customerId: mobile10,
                orderId: form.orderId,
                issueType: form.issueType,
                category: categoryForIssue(form.issueType),
                description: form.description,
                source: form.source,
                priority: effPriority,
                assignedDepartment: effDept,
                assignedTo: form.assignedTo,
                followUpAt: form.followUpAt ? new Date(form.followUpAt).getTime() : undefined,
                productNames: form.productNames ? form.productNames.split(',').map(s => s.trim()).filter(Boolean) : [],
            }, currentUser, { isRepeatCustomer: repeat });
            if (files.length) {
                try {
                    const atts = await Promise.all(files.map(f => TicketService.uploadAttachment(created.id, f)));
                    await TicketService.updateTicket(created.id, { attachments: atts });
                } catch (e2) { alert('Ticket created, but attachment upload failed: ' + e2.message); }
            }
            onCreated?.(created);
            onClose();
        } catch (e) {
            alert('Failed to create ticket: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create Ticket" className="max-w-2xl">
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1" onPaste={onPasteImage}>
                {repeat && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-2">
                        <UserCheck size={16} /> Customer has {countCustomerComplaints(form.mobile, existingTickets)} previous complaint(s) — Repeat Complaint Customer.
                    </div>
                )}
                {duplicate && (
                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-2">
                        <AlertTriangle size={16} /> Possible duplicate of {duplicate.ticketNumber}. Consider merging instead of creating a new ticket.
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Mobile *</label>
                        <input className={inputCls} value={form.mobile} onBlur={autoFetchCustomer}
                            onChange={e => set('mobile', e.target.value)} placeholder="9XXXXXXXXX" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Customer Name</label>
                        <input className={inputCls} value={form.customerName} onChange={e => set('customerName', e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Order ID</label>
                        <input className={inputCls} value={form.orderId} onChange={e => set('orderId', e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Product(s) (comma-separated)</label>
                        <input className={inputCls} value={form.productNames} onChange={e => set('productNames', e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Issue Type</label>
                        {/* 46 options is too many to scroll while a customer
                            waits — type a number or a few letters instead. */}
                        <NumberedSearchSelect
                            options={Object.keys(ISSUE_TYPES)}
                            value={form.issueType}
                            onChange={(v) => set('issueType', v)}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Source</label>
                        <select className={inputCls} value={form.source} onChange={e => set('source', e.target.value)}>
                            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Priority <span className="text-slate-400">(auto: {effPriority})</span></label>
                        <select className={inputCls} value={form.priority} onChange={e => set('priority', e.target.value)}>
                            <option value="">Auto ({effPriority})</option>
                            {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Department <span className="text-slate-400">(auto: {effDept})</span></label>
                        <select className={inputCls} value={form.assignedDepartment} onChange={e => set('assignedDepartment', e.target.value)}>
                            <option value="">Auto ({effDept})</option>
                            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Assign To (uid/email)</label>
                        <input className={inputCls} value={form.assignedTo} onChange={e => set('assignedTo', e.target.value)} />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-500">Follow-up date/time</label>
                        <input type="datetime-local" className={inputCls} value={form.followUpAt} onChange={e => set('followUpAt', e.target.value)} />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-semibold text-slate-500">Description (copy-paste friendly)</label>
                    <textarea className={inputCls} rows={4} value={form.description} onChange={e => set('description', e.target.value)} />
                </div>
                <div>
                    <label className="text-xs font-semibold text-slate-500">Attachments <span className="text-slate-400 font-normal">(optional — or paste a screenshot)</span></label>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <label className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-purple-600 cursor-pointer border border-dashed border-slate-300 rounded-lg px-2.5 py-1.5">
                            📎 Add photos / files
                            <input type="file" multiple className="hidden" onChange={e => { setFiles([...files, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                        </label>
                        {files.map((f, i) => (
                            <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-slate-100 rounded-lg px-1.5 py-1 max-w-[150px]">
                                {String(f.type || '').startsWith('image/') ? <img src={URL.createObjectURL(f)} alt="" className="h-6 rounded" /> : '📄'}
                                <span className="truncate">{f.name}</span>
                                <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500">✕</button>
                            </span>
                        ))}
                    </div>
                </div>

                {priorComplaints.length > 0 && (
                    <div className="text-xs text-slate-500">
                        <span className="font-semibold">Previous complaints:</span> {priorComplaints.slice(0, 5).map(t => t.ticketNumber).join(', ')}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !form.mobile.trim()}>{saving ? 'Creating…' : 'Create Ticket'}</Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateTicketModal;
